import {Box, Divider, Tab, Tabs} from '@mui/material';
import {useOptimisticSearchParams} from 'nuqs/adapters/react-router/v7';
import {type FC} from 'react';
import {NavLink, Outlet, Route, Routes, useParams} from 'react-router';
import {
  POINTER_EVENTS_PASSING_SX,
  SidePanel,
} from '@src/components/panels/SidePanels';
import Browse from '@src/components/panels/browse/Browse';
import LayersPanel from '@src/components/visualize/LayersPanel';
import SettingsAndEffectsPanel from '@src/components/visualize/SettingsAndEffectsPanel';
import Visualize from '@src/components/visualize/Visualize';
import VisualizeActions from '@src/components/visualize/VisualizeActions';
import LayersPanelTitle from '@src/components/visualize/layers/LayersPanelTitle';
import {ANALYZE_ROOT_ROUTE, ANALYZE_ROUTE_KEYS} from '@src/constants/routes';
import useVisualizationParams from '@src/hooks/useVisualizationParams';
import dimensions from '@src/styles/dimensions';

export type AnalyzeValuesType =
  (typeof ANALYZE_ROUTE_KEYS)[keyof typeof ANALYZE_ROUTE_KEYS];

const Analyze: FC = () => {
  const {'*': splatPath} = useParams() as {'*': string};
  const pathParts = splatPath?.split('/');
  const {configurationId, layerId} = useVisualizationParams();
  const activeTabValue: AnalyzeValuesType =
    (pathParts[0] as AnalyzeValuesType) ?? ANALYZE_ROUTE_KEYS.BROWSE;
  const search = useOptimisticSearchParams() ?? new URLSearchParams();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        maxBlockSize: `calc(100% - ${dimensions.statusbar.height}px - ${dimensions.map.toolMargin}px)`,
        ...POINTER_EVENTS_PASSING_SX,
      }}
    >
      <SidePanel
        title="Visual analysis"
        rootProps={{
          sx: {
            flexShrink: 0,
            // Analyze edge case to override of standard full height
            blockSize: `auto`,
            maxBlockSize: `calc(100% - ${dimensions.map.toolMargin}px)`,
          },
        }}
      >
        <Tabs
          value={activeTabValue}
          sx={theme => ({
            blockSize: dimensions.sidebarTab.height,
            backgroundImage: theme.vars?.overlays[1],
          })}
          aria-label="Analyze tabs"
        >
          <Tab
            value={ANALYZE_ROUTE_KEYS.BROWSE}
            label="Browse"
            component={NavLink}
            to={{
              pathname: `/${ANALYZE_ROOT_ROUTE}/${ANALYZE_ROUTE_KEYS.BROWSE}`,
              search: search.toString(),
            }}
          />
          <Tab
            disabled={!layerId}
            value={ANALYZE_ROUTE_KEYS.VISUALIZE}
            label="Visualize"
            component={NavLink}
            to={{
              pathname: `/${ANALYZE_ROOT_ROUTE}/${ANALYZE_ROUTE_KEYS.VISUALIZE}`,
              search: search.toString(),
            }}
          />
        </Tabs>
        <Divider />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            overflowBlock: 'hidden',
            p: `${dimensions.leftSidebar.contentMargin}px`,
          }}
        >
          <Routes>
            <Route path={ANALYZE_ROUTE_KEYS.BROWSE} element={<Browse />} />
            <Route
              path={`${ANALYZE_ROUTE_KEYS.VISUALIZE}/*`}
              element={<Visualize />}
            />
          </Routes>
        </Box>
      </SidePanel>
      <Routes>
        <Route
          path={`${ANALYZE_ROUTE_KEYS.VISUALIZE}/*`}
          element={
            <SidePanel
              title={<LayersPanelTitle />}
              headerProps={{
                contentProps: {
                  sx: {
                    maxInlineSize: '100%',
                  },
                },
              }}
              showHeader={!!configurationId}
              rootProps={{
                sx: {
                  blockSize: `auto`,
                },
              }}
            >
              <Box
                sx={{
                  p: `${dimensions.leftSidebar.contentMargin}px`,
                  overflowBlock: 'auto',
                }}
              >
                <Outlet />
              </Box>
              <VisualizeActions />
            </SidePanel>
          }
        >
          <Route index element={<LayersPanel />} />
          <Route
            path={ANALYZE_ROUTE_KEYS.EFFECTS}
            element={<SettingsAndEffectsPanel />}
          />
        </Route>
      </Routes>
    </Box>
  );
};

export default Analyze;
